import { Button, Divider, Icon, Intent, Position, Tooltip } from "@blueprintjs/core"
import { useState } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { ID } from "src/libs/interfaces/layouts"
import Shortcode from "src/libs/useful/shortcode"
import { __ } from "src/libs/utilities"
import { HISTORY_TYPE, IHistoryStep, ILanding, ILandingContent, ILandingSection } from "src/modules/pe-landing-module/data/interfaces"
import { ILandingStore, useLandingConsoleStore, useLandingStore } from "src/modules/pe-landing-module/data/store"
import { components } from "src/modules/pe-landing-module/data/utils/components"
import { getLandingContainer } from "../../../utils/getLandingContainer"
import updateSection from "../utils/updateSection"
import FloatsLayer from "./FloatsLayer"
import SingleMeatLayer from "./SingleMeatLayer"
import TitleLayer from "./TitleLayer"


interface ISectionLayer {
    section: ILandingSection 
}
const SingleSectionLayer =({section, ...props}: ISectionLayer) : JSX.Element => {
    const currentLanding:ILanding   = useLandingStore((state: ILandingStore) => state.currentLanding)    
    const selectedLayer : ID        = useLandingStore( (state:ILandingStore) => state.selectedLayer ) 
    const [isOpen, setIsOpen]       = useState<boolean>( false )

    const slideTo = () => {
        const landingCont = getLandingContainer()
        const element = landingCont.querySelector( `.landing-section-container.landing-gap[id="${section.id.toString()}"]` )
        if(!!element) { 
            const lScroll = landingCont.scrollTop
            landingCont.scrollTo({
                behavior: 'smooth',
                top: lScroll + element.getBoundingClientRect().top,
            })
        } 
        useLandingStore.setState({ 
            selectedLayer: section.id,
            selectedMeat:  section.data[0]?.id || ""
        })
        
        const state = { 
            isMeat: true,
        } 
        useLandingConsoleStore.setState(state)
    }

    const onVisible = () => {
        const newLanding = {
            ...currentLanding,
            sections: currentLanding.sections.map((sec, i) => {
                if( sec.id === section.id ) {
                    return {
                        ...sec,
                        isHidden: !currentLanding.sections[i].isHidden
                    } as ILandingSection
                }
                return sec
            })
        }        
        useLandingStore.setState({ currentLanding: newLanding, needUpdate: true })
    } 
    const getDefaultTitle = () => {
        if( !section.data.length) {
            return undefined
        }
        return section.data[0].defaultTitle
    }
    return <>
        <div className={ `panel-list-button pl-0 ${ selectedLayer === section.id ? "active" : "" }` } onClick={ slideTo }>
            <Icon icon="drag-handle-vertical" className="pl-1 pr-1 pe-surface-4 hover " size={13}/>
            <Button variant="minimal" onClick={ () => setIsOpen(!isOpen) }>
                <i className={ isOpen ? "fas fa-chevron-down" : "fas fa-chevron-right" }></i>
            </Button>
            <Shortcode 
                className="small text-light text-overflow flex-grow-100 px-1" 
                content={ 
                    section.title?.text || 
                    getDefaultTitle() ||
                    __( "Section" ) + " <span class='opacity_5 small'>#" + section.id + "</span>" 
                }  
            />                
            {
                section.data?.map(data => {
                    return <Tooltip
                        key={data.id}
                        content={<div className="small">{__( components()[data.type].title )}</div>}
                        intent={Intent.PRIMARY}
                        position={Position.TOP_RIGHT}
                        compact
                    >
                        <div >
                            <img src={components()[data.type].icon} alt="" height={16} className="panel-icon pr-1" />
                        </div>
                    </Tooltip>
                })
            }
            <Tooltip 
                content={<div className="small">{__("Floats")}</div>}
                intent={Intent.PRIMARY}
                position={Position.TOP_RIGHT}
                compact
            >
                <div className=" float-indicator " >
                    { (section.floats?.length || 0) }
                </div> 
            </Tooltip>
            
            <Tooltip 
                content={<div className="small">{ __( !section.isHidden ?  "hidden" : "visible" ) }</div>}
                intent={Intent.PRIMARY}
                position={Position.TOP_RIGHT}
                compact
            >
                <Button variant="minimal"  onClick={onVisible}  >
                    <i className={ !section.isHidden ?  "far fa-eye" : "far fa-eye-slash"  }></i> 
                </Button> 
            </Tooltip>
        </div>
            {
                isOpen && <>
                    <TitleLayer section={ section } />
                    <MeatList section={ section } /> 
                    <FloatsLayer floats={section.floats} sectionId={section.id}  /> 
                </>
            }
        <Divider />
    </>
}
export default SingleSectionLayer

const MeatList = (props : any): JSX.Element => { 
    
    // a little function to help us with reordering the result
    const reorder = (list: any[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed); 
        return result;
    }; 

    const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({ 
        userSelect: "none",
        padding: 0, 
        margin: `0 0 0 0`, 
        width: "100%",
        // change background colour if dragging
        background: isDragging ? "#45526d" : "transparent", 
        // styles we need to apply on draggables
        ...draggableStyle
    });

    const getListStyle = (isDraggingOver: boolean) => ({
        background: isDraggingOver ? "#3180e7cc" : "#213146cc",
        padding: 0, 
    })
    const onDragEnd = (result: any) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        } 
        const _items: any[] = reorder(
            props.section.data,
            result.source.index,
            result.destination.index
        ); 
        const newSection = JSON.parse( JSON.stringify( {
            ...props.section,
            data: _items
        } ) )

        const step: IHistoryStep = {
            data : newSection,
            prevData: props.section,
            type: HISTORY_TYPE.SECTION
        }
        updateSection(newSection, step) 
    }
    return <div>
        {
            !!props.section.data.length && <div className="micro letter-spacing-5 text-center text-light p-1" style={{backgroundColor: "#213146cc"}}>
                <div className="text-uppercase smaller title opacity_5">
                    {__( "Meats" )}
                </div>
            </div>
        }
        <DragDropContext onDragEnd={ onDragEnd }>
            <Droppable droppableId="droppable">
                {( provided, snapshot ) => (
                    <div
                        { ...provided.droppableProps }
                        ref={ provided.innerRef }
                        style={ getListStyle(snapshot.isDraggingOver) }
                    >
                        { 
                            props.section.data.map(( data: ILandingContent, index: number ) => {
                                return <div key={ data.id } className="d-flex"> 
                                    <Draggable draggableId={ data.id.toString() } index={ index }>
                                        {( provided, snapshot ) => ( 
                                            <div
                                                ref={ provided.innerRef }
                                                { ...provided.draggableProps }
                                                { ...provided.dragHandleProps }
                                                style={ getItemStyle(
                                                    snapshot.isDragging,
                                                    provided.draggableProps.style
                                                ) }
                                            > 
                                                <SingleMeatLayer data={ data } sectionId={ props.section.id } />
                                            </div>
                                        )}
                                    </Draggable>
                                </div>
                            })
                        }
                    </div>
                )}
            </Droppable >
        </DragDropContext> 
    </div> 
}